/* eslint complexity: ["error", 29]*/
/* eslint max-lines-per-function: ["error", 288]*/
/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon2 from 'react-icons/tb';

import { useTranslation } from '../../../translations';
import { useNotifications } from '../../hooks/useNotifications';
import { ResponseError } from '../../pokCore/autogenerated/pokApiClient';
import { Waiting } from '../Waiting/Waiting';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './ReactPdf.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const options = {
  cMapUrl: `https://unpkg.com/browse/pdfjs-dist@${pdfjs.version}/cmaps/`,
  standardFontDataUrl: `https://unpkg.com/browse/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
};

const patterns = [
  'Numer zlecenia (\\d{3,11})/i',
  'Należność netto za emisje:\\s*([\\d\\s]+,\\d{2}) zł',
  'Do zapłaty:\\s*([\\d\\s]+,\\d{2})',
  'Razem do zapłaty\\s*([\\d\\s]+,\\d{2}) zł',
  'Kod zlecenia:\\s*([A-Z]+\\/\\d+\\/\\d{4})',
];
const patternToHighlight = new RegExp(patterns.join('|'), 'gi');

export const PdfPlaceholder: React.FC<{ width?: number }> = ({ width }) => (
  <div
    className="pdf-viewer__placeholder"
    style={{ width: width || undefined }}
  >
    <Waiting />
  </div>
);

export interface PdfPageInfo {
  pageNumber: number;
  widthPt: number;
  heightPt: number;
}

interface ReactPdfProps {
  file: Blob;
  pageNumber?: number;
  onPageNumberChange?: (pageNumber: number) => void;
  onNumPagesChange?: (numPages: number) => void;
  onPageInfoChange?: (page: PdfPageInfo) => void;
  header?: React.ReactNode;
  renderPageOverlay?: (page: PdfPageInfo) => React.ReactNode;
}

export const ReactPdf: React.FC<ReactPdfProps> = props => {
  const [numPages, setNumPages] = useState<number>(1);
  const [scale, setScale] = useState(1);
  const [innerPageNumber, setInnerPageNumber] = useState(1);
  const [pageInfo, setPageInfo] = useState<PdfPageInfo>();
  const [onlyImage, setOnlyImage] = useState(false);
  const [width, setWidth] = useState<number>(0);
  const [renderedPageHeight, setRenderedPageHeight] = useState<number>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [cursorMode, setCursorMode] = useState<'default' | 'grab'>('default');
  const startPosition = useRef<{ x: number; y: number } | null>(null);
  const scrollPosition = useRef<{ left: number; top: number } | null>(null);

  const { t, tk } = useTranslation('commonLng');
  const notifications = useNotifications();
  const pageNumber = props.pageNumber ?? innerPageNumber;
  const setPageNumber = (value: number) => {
    setInnerPageNumber(value);
    props.onPageNumberChange?.(value);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const updateWidth = () => {
      const clientWidth = container.clientWidth;
      if (clientWidth > 0) {
        setWidth(clientWidth - 2);
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    props.onNumPagesChange?.(numPages);
    setPageNumber(1);
  }

  function changePage(offset: number) {
    setPageNumber(pageNumber + offset);
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  const changeScale = (delta: number) => {
    setScale(prevScale => Math.min(Math.max(0.5, prevScale + delta), 3.6));
  };

  const changeCursorMode = () => {
    setCursorMode(prev => (prev === 'default' ? 'grab' : 'default'));
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (cursorMode === 'grab' && containerRef.current) {
      setIsDragging(true);
      startPosition.current = { x: event.clientX, y: event.clientY };
      scrollPosition.current = {
        left: containerRef.current.scrollLeft,
        top: containerRef.current.scrollTop,
      };
    }
  };

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (
        !isDragging ||
        !startPosition.current ||
        !scrollPosition.current ||
        !containerRef.current
      ) {
        return;
      }

      const dx = event.clientX - startPosition.current.x;
      const dy = event.clientY - startPosition.current.y;
      containerRef.current.scrollLeft = scrollPosition.current.left - dx;
      containerRef.current.scrollTop = scrollPosition.current.top - dy;
    },
    [isDragging],
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', () => setIsDragging(false));
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', () => setIsDragging(false));
    };
  }, [handleMouseMove, isDragging]);

  return (
    <div className="pdf-viewer pdf-viewer__scroll_x">
      {props.header}
      <div className="pdf-viewer__controls">
        <div className="pdf-viewer__controls-left">
          <Button
            variant="outline-secondary"
            disabled={pageNumber <= 1}
            onClick={previousPage}
          >
            <Icon2.TbChevronLeft size={18} />
          </Button>
          <span>
            {t(tk.pdfViewer.page)} {pageNumber || (numPages ? 1 : '--')}{' '}
            {t(tk.pdfViewer.of)} {numPages || '--'}
          </span>

          <Button
            variant="outline-secondary"
            disabled={pageNumber >= numPages}
            onClick={nextPage}
          >
            <Icon2.TbChevronRight size={18} />
          </Button>
          {onlyImage && (
            <span className="text-red ">{t(tk.pdfViewer.noTextCopy)}</span>
          )}
        </div>

        <div className="pdf-viewer__controls-right">
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip>
                {cursorMode === 'default'
                  ? t(tk.pdfViewer.pan)
                  : t(tk.pdfViewer.select)}
              </Tooltip>
            }
          >
            <Button variant="outline-secondary" onClick={changeCursorMode}>
              {cursorMode === 'default' ? (
                <Icon2.TbHandStop size={18} />
              ) : (
                <Icon2.TbPointer size={18} />
              )}
            </Button>
          </OverlayTrigger>
          <Button variant="outline-secondary" onClick={() => setScale(1)}>
            <Icon2.TbZoomReset />
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => changeScale(scale < 2 ? -0.1 : -0.2)}
            disabled={scale <= 0.5}
          >
            <Icon2.TbZoomOut size={18} />
          </Button>
          <span className="pdf-viewer__scale-display">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="outline-secondary"
            onClick={() => changeScale(scale < 2 ? 0.1 : 0.2)}
            disabled={scale >= 3.6}
          >
            <Icon2.TbZoomIn size={18} />
          </Button>
        </div>
      </div>
      <div
        className="pdf-viewer__scroll_x pdf-viewer__scroll_y"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        style={{
          cursor:
            cursorMode === 'grab'
              ? isDragging
                ? 'grabbing'
                : 'grab'
              : 'default',
        }}
      >
        <Document
          file={props.file}
          onLoadSuccess={onDocumentLoadSuccess}
          onSourceError={error => {
            notifications.caughtError(error as ResponseError);
          }}
          options={options}
          loading={<PdfPlaceholder />}
          noData={t(tk.pdfViewer.noPdfFile)}
          error={t(tk.pdfViewer.error)}
        >
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
              minHeight: renderedPageHeight,
            }}
          >
            <Page
              renderTextLayer={!isDragging}
              width={width}
              pageNumber={pageNumber}
              loading={<PdfPlaceholder width={width} />}
              customTextRenderer={({ str }) => {
                return str.replace(
                  patternToHighlight,
                  value =>
                    `<mark style="border: 2px solid red; border-radius: 4px; padding: 2px 4px; color: transparent; background-color: transparent;">${value}</mark>`,
                );
              }}
              scale={scale}
              onRenderSuccess={page => setRenderedPageHeight(page.height)}
              onLoadSuccess={page => {
                const info = {
                  pageNumber: page.pageNumber,
                  widthPt: page.originalWidth,
                  heightPt: page.originalHeight,
                };
                setPageInfo(info);
                setRenderedPageHeight(page.height);
                props.onPageInfoChange?.(info);
              }}
              onGetTextSuccess={textContent => {
                if (textContent.items.length === 0) {
                  setOnlyImage(true);
                } else {
                  setOnlyImage(false);
                }
              }}
            />
            {props.renderPageOverlay &&
              pageInfo?.pageNumber === pageNumber &&
              props.renderPageOverlay(pageInfo)}
          </div>
        </Document>
      </div>
    </div>
  );
};
